import { FC, SyntheticEvent, useEffect, useRef, useState } from "react";

import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  IconButton,
  InputAdornment,
  ListItem,
  Popper,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { ClearIcon } from "@mui/x-date-pickers";

import { useGetLocationsCached } from "../../../../pages/customer-settings/tabs/locations/hooks/useGetLocationsCached";
import { AutocompleteOptionType } from "../../../models/autocomplete";
import LocationIcon from "../../icons/LocationIcon";

interface LocationSelectProps {
  defaultValue?: AutocompleteOptionType | null;
  value: AutocompleteOptionType | null;
  onChange: (value: AutocompleteOptionType | null) => void;
  disabled?: boolean;
  size?: "small" | "medium";
  includeAllLocations?: boolean;
}

const ALL_LOCATIONS_OPTION: AutocompleteOptionType = {
  title: "All locations",
  value: "",
};

const LocationSelect: FC<LocationSelectProps> = ({
  defaultValue,
  value,
  onChange,
  disabled = false,
  size = "small",
  includeAllLocations = true,
}) => {
  const hoveredRef = useRef<string | null>(null);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (defaultValue?.title) {
      onChange(defaultValue ?? ALL_LOCATIONS_OPTION);
    }
  }, []);

  const { locations, loading } = useGetLocationsCached();

  const locationOptions: AutocompleteOptionType[] = locations.map(loc => ({
    title: loc.name ?? "",
    value: loc.id ?? "",
  }));

  const options: AutocompleteOptionType[] = includeAllLocations
    ? [ALL_LOCATIONS_OPTION, ...locationOptions]
    : locationOptions;

  const handleChange = (
    _: SyntheticEvent,
    newValue: AutocompleteOptionType | null
  ) => {
    if (!disabled) {
      onChange(newValue && newValue.value !== "" ? newValue : null);
    }
  };

  const handleClear = () => {
    if (!disabled) {
      onChange(null);
    }
  };

  const handleOnOpen = () => {
    setOpen(true);
  };

  const handleOnClose = () => {
    setOpen(false);

    hoveredRef.current = null;
  };

  const handleOnMouseEnter = (option: AutocompleteOptionType) => {
    hoveredRef.current = option?.value ?? null;
  };

  const handleOnMouseLeave = () => {
    hoveredRef.current = null;
  };

  return (
    <Autocomplete
      fullWidth
      size={size}
      loading={loading}
      options={options}
      getOptionLabel={(option: AutocompleteOptionType) => option?.title || ""}
      value={value ?? ALL_LOCATIONS_OPTION}
      onChange={handleChange}
      disabled={disabled}
      popupIcon={<ExpandMore />}
      open={open}
      onOpen={handleOnOpen}
      onClose={handleOnClose}
      disableClearable
      renderOption={(props, option) => (
        <ListItem
          {...props}
          onMouseEnter={() => handleOnMouseEnter(option)}
          onMouseLeave={handleOnMouseLeave}
          sx={{
            bgcolor:
              hoveredRef.current === option?.title ? "inherit" : "transparent",
            "&.Mui-focused": { bgcolor: "transparent" },
            "&[aria-selected='true']": { bgcolor: "transparent" },
          }}
        >
          <Typography>{option?.title}</Typography>
        </ListItem>
      )}
      renderInput={params => (
        <TextField
          {...params}
          placeholder="Select locations"
          variant="outlined"
          label={
            value?.title === ALL_LOCATIONS_OPTION.title
              ? undefined
              : "Locations"
          }
          InputLabelProps={{
            shrink: value?.title !== ALL_LOCATIONS_OPTION.title,
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment
                position="start"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  pl: 0,
                }}
              >
                <LocationIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {value && value.value !== null && !disabled && (
                  <IconButton onClick={handleClear} size={size} sx={{ p: 0 }}>
                    <ClearIcon fontSize={size} />
                  </IconButton>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      PopperComponent={props => (
        <Popper
          {...props}
          placement="bottom-start"
          modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
        />
      )}
      isOptionEqualToValue={(option, val) => option?.value === val?.value}
    />
  );
};

export default LocationSelect;
